import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { feedbacksApi, Feedback, FeedbackTask } from '../../api/feedbacksApi';
import { useLang } from '../../contexts/LangContext';
import toast from 'react-hot-toast';
import { getStaffMembers, createTask } from '../../api/tasksApi';
import { User } from '../../types';
import { fileAccessApi } from '../../api/fileAccessApi';
import { SecureAttachment } from '../../components/SecureAttachment';
import { ImageModal } from '../../components/ImageModal';
import { FeedbackMessagePanel } from '../../components/feedbacks/FeedbackMessagePanel';
import { getFeedbackPriorityLabel, getFeedbackStatusLabel } from '../../utils/feedbackLabels';

const FeedbackDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useLang();
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  
  // New states for enhanced workflow
  const [showCompletionConfirm, setShowCompletionConfirm] = useState(false);
  const [completionCountdown, setCompletionCountdown] = useState(5);
  const [completing, setCompleting] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  
  // Sub-task states
  const [subTasks, setSubTasks] = useState<FeedbackTask[]>([]);
  const [loadingSubTasks, setLoadingSubTasks] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [deadline, setDeadline] = useState('');
  
  // Refs for timers
  const isComponentMountedRef = useRef(true);
  const completionTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Modal state for sub-task creation
  const [showSubTaskModal, setShowSubTaskModal] = useState(false);
  const [subTaskTitle, setSubTaskTitle] = useState('');
  const [subTaskDescription, setSubTaskDescription] = useState('');
  const [subTaskAssignee, setSubTaskAssignee] = useState('');
  const [subTaskDueDate, setSubTaskDueDate] = useState('');
  const [subTaskLoading, setSubTaskLoading] = useState(false);
  const [staffList, setStaffList] = useState<User[]>([]);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

  useEffect(() => {
    if (id) {
      loadFeedbackDetails();
      loadSubTasks();
    }
    
    // Cleanup function
    return () => {
      isComponentMountedRef.current = false;
      if (completionTimerRef.current) {
        clearInterval(completionTimerRef.current);
      }
    };
  }, [id]);

  // Handle completion countdown
  useEffect(() => {
    if (showCompletionConfirm && completionCountdown > 0) {
      completionTimerRef.current = setInterval(() => {
        setCompletionCountdown(prev => {
          if (prev <= 1) {
            // Auto-complete when countdown reaches 0
            confirmCompleteFeedback();
            return 5;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (completionTimerRef.current) {
        clearInterval(completionTimerRef.current);
      }
    };
  }, [showCompletionConfirm, completionCountdown]);

  // Handle keyboard events for modal
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && imageModalOpen) {
        closeImageModal();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [imageModalOpen]);

  // Fetch staff for assignee dropdown
  useEffect(() => {
    if (showSubTaskModal) {
      getStaffMembers().then(setStaffList).catch(() => setStaffList([]));
    }
  }, [showSubTaskModal]);

  const loadFeedbackDetails = async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);
      const feedbackData = await feedbacksApi.getFeedback(id);
      setFeedback(feedbackData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteFeedback = () => {
    setShowCompletionConfirm(true);
    setCompletionCountdown(5);
  };

  const confirmCompleteFeedback = useCallback(async () => {
    if (!feedback || completing) return;

    setCompleting(true);
    try {
      await feedbacksApi.completeFeedback(feedback.id);
      toast.success('Phản ánh đã được hoàn thành!');
      await loadFeedbackDetails();
    } catch (error) {
      toast.error('Lỗi khi hoàn thành phản ánh');
    } finally {
      setCompleting(false);
      setShowCompletionConfirm(false);
    }
  }, [feedback, completing]);

  // Open modal instead of navigating
  const handleCreateTask = () => {
    setShowSubTaskModal(true);
  };

  // Submit sub-task creation
  const handleSubTaskSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!subTaskTitle || !subTaskAssignee || !subTaskDueDate) return;
    setSubTaskLoading(true);
    try {
      // 1. Create the task
      const newTask = await createTask({
        title: subTaskTitle,
        description: subTaskDescription,
        assignedToId: subTaskAssignee,
        dueDate: subTaskDueDate,
      });
      // 2. Link to feedback as sub-task
      if (feedback?.id) {
        await feedbacksApi.addFeedbackTask(feedback.id, newTask.id);
        await loadSubTasks();
      }
      setShowSubTaskModal(false);
      setSubTaskTitle('');
      setSubTaskDescription('');
      setSubTaskAssignee('');
      setSubTaskDueDate('');
      toast.success('Đã tạo công việc con!');
    } catch (err) {
      toast.error('Lỗi khi tạo công việc con');
    } finally {
      setSubTaskLoading(false);
    }
  };

  const loadSubTasks = async () => {
    if (!id) return;
    
    try {
      setLoadingSubTasks(true);
      const tasksData = await feedbacksApi.getFeedbackTasks(id);
      setSubTasks(tasksData);
    } catch (error) {
      console.warn('Failed to load sub-tasks:', error);
      setSubTasks([]);
    } finally {
      setLoadingSubTasks(false);
    }
  };

  const handleConfirmFeedback = () => {
    setShowConfirmModal(true);
  };

  const confirmFeedback = async () => {
    if (!feedback || confirming || !deadline) return;

    setConfirming(true);
    try {
      await feedbacksApi.confirmFeedback(feedback.id, { deadline });
      toast.success('Phản ánh đã được xác nhận!');
      await loadFeedbackDetails();
      setShowConfirmModal(false);
      setDeadline('');
    } catch (error) {
      toast.error('Lỗi khi xác nhận phản ánh');
    } finally {
      setConfirming(false);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'URGENT': return 'bg-red-100 text-red-800';
      case 'HIGH': return 'bg-orange-100 text-orange-800';
      case 'MEDIUM': return 'bg-yellow-100 text-yellow-800';
      case 'LOW': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING_CONFIRMATION': return 'bg-yellow-100 text-yellow-800';
      case 'CONFIRMED': return 'bg-blue-100 text-blue-800';
      case 'IN_PROGRESS': return 'bg-purple-100 text-purple-800';
      case 'COMPLETED': return 'bg-green-100 text-green-800';
      case 'REJECTED': return 'bg-red-100 text-red-800';
      case 'CANCELLED': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => getFeedbackStatusLabel(status, t);

  const getPriorityText = (priority: string) => getFeedbackPriorityLabel(priority, t);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('vi-VN');
  };

  const getFileIcon = (fileName: string) => {
    const extension = fileName.toLowerCase().split('.').pop() || '';
    switch (extension) {
      case 'pdf': return '📄';
      case 'doc':
      case 'docx': return '📝';
      case 'xls':
      case 'xlsx': return '📊';
      case 'ppt':
      case 'pptx': return '📋';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'webp': return '🖼️';
      case 'mp4':
      case 'avi':
      case 'mov': return '🎥';
      case 'mp3':
      case 'wav': return '🎵';
      case 'zip':
      case 'rar': return '📦';
      default: return '📎';
    }
  };

  const isImageFile = (fileName: string) => {
    const extension = fileName.toLowerCase().split('.').pop();
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(extension || '');
  };

  const getAttachmentUrl = async (fileName: string): Promise<string> => {
    // If the fileName already starts with http/https, it's a complete URL (S3)
    if (fileName.startsWith('http://') || fileName.startsWith('https://')) {
      return fileName;
    }
    
    // If it's an S3 reference URL, get a pre-signed URL
    if (fileName.startsWith('s3://')) {
      try {
        return await fileAccessApi.getSecureFileUrl(fileName);
      } catch (error) {
        console.error('Error getting secure file URL:', error);
        return fileName; // Fallback to original URL
      }
    }
    
    // Otherwise, it's a local file path, prepend the API base URL
    return `${API_BASE_URL}/feedbacks/attachments/${fileName}`;
  };

  const openImageModal = (imageUrl: string) => {
    setSelectedImage(imageUrl);
    setImageModalOpen(true);
  };

  const closeImageModal = () => {
    setImageModalOpen(false);
    setSelectedImage(null);
  };

  const renderAttachment = (fileName: string, index: number) => {
    const isImage = isImageFile(fileName);
    
    return (
      <SecureAttachment
        key={index}
        fileName={fileName}
        index={index}
        isImage={isImage}
        onImageClick={openImageModal}
        getFileIcon={getFileIcon}
      />
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="text-red-800">Lỗi: {error}</div>
        <button
          onClick={() => navigate('/feedbacks')}
          className="mt-2 text-blue-600 hover:text-blue-800"
        >
          Quay lại danh sách
        </button>
      </div>
    );
  }

  if (!feedback) {
    return (
      <div className="text-center py-8">
        <div className="text-gray-500">Không tìm thấy phản ánh</div>
        <button
          onClick={() => navigate('/feedbacks')}
          className="mt-2 text-blue-600 hover:text-blue-800"
        >
          Quay lại danh sách
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/feedbacks')}
          className="flex items-center text-blue-600 hover:text-blue-800 mb-4"
        >
          <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Quay lại danh sách
        </button>
        <div className="flex justify-between items-start">
          <h1 className="text-2xl font-bold text-gray-900">Chi tiết phản ánh #{feedback.feedbackId}</h1>
          
          {/* Action Buttons */}
          <div className="flex space-x-3">
            {/* Confirm Button - Only for PENDING_CONFIRMATION status */}
            {feedback.status === 'PENDING_CONFIRMATION' && (
              <button
                onClick={handleConfirmFeedback}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                title="Xác nhận phản ánh"
              >
                <svg className="w-4 h-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Xác nhận
              </button>
            )}
            
            {/* Task Creation Button - For CONFIRMED or IN_PROGRESS status */}
            {(feedback.status === 'CONFIRMED' || feedback.status === 'IN_PROGRESS') && (
              <button
                onClick={handleCreateTask}
                className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors"
                title="Tạo công việc con cho phản ánh này"
              >
                <svg className="w-4 h-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Tạo công việc
              </button>
            )}
            
            {/* Start Work Button - For CONFIRMED status with no sub-tasks */}
            {feedback.status === 'CONFIRMED' && subTasks.length === 0 && (
              <button
                onClick={async () => {
                  if (!feedback.id) return;
                  try {
                    await feedbacksApi.updateFeedbackStatus(feedback.id, 'IN_PROGRESS');
                    toast.success('Phản ánh đã chuyển sang trạng thái Đang xử lý!');
                    await loadFeedbackDetails();
                  } catch (err) {
                    toast.error('Lỗi khi chuyển trạng thái');
                  }
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                title="Bắt đầu xử lý phản ánh này"
              >
                <svg className="w-4 h-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Bắt đầu xử lý
              </button>
            )}
            
            {/* Completion Button - Only for IN_PROGRESS status */}
            {feedback.status === 'IN_PROGRESS' && (
              <button
                onClick={handleCompleteFeedback}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                title="Hoàn thành phản ánh"
              >
                <svg className="w-4 h-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Hoàn thành
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Feedback Details */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-semibold mb-4">Thông tin phản ánh</h3>
            <div className="space-y-3">
              <div>
                <span className="text-gray-600">Mã phản ánh:</span>
                <span className="ml-2 font-medium">{feedback.feedbackId}</span>
              </div>
              <div>
                <span className="text-gray-600">Phân loại:</span>
                <span className="ml-2">{feedback.category.name}</span>
              </div>
              <div>
                <span className="text-gray-600">Mức độ ưu tiên:</span>
                <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(feedback.priority)}`}>
                  {getPriorityText(feedback.priority)}
                </span>
              </div>
              <div>
                <span className="text-gray-600">Trạng thái:</span>
                <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(feedback.status)}`}>
                  {getStatusText(feedback.status)}
                </span>
              </div>
              <div>
                <span className="text-gray-600">Ngày tạo:</span>
                <span className="ml-2">{formatDate(feedback.createdAt)}</span>
              </div>
              {feedback.deadline && (
                <div>
                  <span className="text-gray-600">Hạn xử lý:</span>
                  <span className="ml-2">{formatDate(feedback.deadline)}</span>
                </div>
              )}
              {feedback.confirmedBy && (
                <div>
                  <span className="text-gray-600">Người duyệt:</span>
                  <span className="ml-2">{feedback.confirmedBy.name}</span>
                </div>
              )}
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-4">Thông tin căn hộ</h3>
            <div className="space-y-3">
              <div>
                <span className="text-gray-600">Căn hộ:</span>
                <span className="ml-2 font-medium">{feedback.apartment.code}</span>
              </div>
              <div>
                <span className="text-gray-600">Chủ hộ:</span>
                <span className="ml-2">{feedback.apartment.ownerName}</span>
              </div>
              <div>
                <span className="text-gray-600">Điện thoại:</span>
                <span className="ml-2">{feedback.apartment.ownerPhone}</span>
              </div>
              <div>
                <span className="text-gray-600">Email:</span>
                <span className="ml-2">{feedback.apartment.ownerEmail}</span>
              </div>
              <div>
                <span className="text-gray-600">Tòa nhà:</span>
                <span className="ml-2">{feedback.building.name}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6">
          <h3 className="text-lg font-semibold mb-3">Nội dung phản ánh</h3>
          <div className="bg-gray-50 rounded-md p-4">
            <p className="text-gray-800 whitespace-pre-wrap">{feedback.content}</p>
          </div>
        </div>

        {/* Enhanced Attachments Display */}
        {feedback.attachments && feedback.attachments.length > 0 && (
          <div className="mt-4">
            <h4 className="text-md font-semibold mb-3">File đính kèm:</h4>
            <div className="space-y-2">
              {feedback.attachments.map((attachment, index) => renderAttachment(attachment, index))}
            </div>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        {id && (
          <FeedbackMessagePanel
            feedbackId={id}
            enablePolling
            disabled={feedback.status === 'PENDING_CONFIRMATION'}
            renderAttachment={renderAttachment}
          />
        )}
      </div>

      {/* Sub-tasks Section */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Công việc con</h3>
          <button
            onClick={loadSubTasks}
            className="flex items-center text-sm text-blue-600 hover:text-blue-800 transition-colors"
            title="Làm mới danh sách công việc"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Làm mới
          </button>
        </div>
        
        {loadingSubTasks ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-500 mt-2">Đang tải công việc con...</p>
          </div>
        ) : subTasks.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            <svg className="w-12 h-12 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <p>Chưa có công việc con nào</p>
            {feedback.status === 'IN_PROGRESS' && (
              <button
                onClick={handleCreateTask}
                className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Tạo công việc đầu tiên
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {subTasks.map((feedbackTask) => (
              <div key={feedbackTask.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <h4 className="font-medium text-gray-900">{feedbackTask.task.title}</h4>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        feedbackTask.task.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                        feedbackTask.task.status === 'IN_PROGRESS' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {feedbackTask.task.status === 'COMPLETED' ? 'Hoàn thành' :
                         feedbackTask.task.status === 'IN_PROGRESS' ? 'Đang thực hiện' : 'Chờ thực hiện'}
                      </span>
                    </div>
                    <p className="text-gray-600 text-sm mb-2">{feedbackTask.task.description}</p>
                    <div className="flex items-center space-x-4 text-xs text-gray-500">
                      <span>Người thực hiện: {feedbackTask.task.assignedTo.name}</span>
                      <span>Hạn: {formatDate(feedbackTask.task.dueDate)}</span>
                      {feedbackTask.task.completedAt && (
                        <span>Hoàn thành: {formatDate(feedbackTask.task.completedAt)}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => navigate(`/tasks/${feedbackTask.task.id}/details`)}
                      className="text-blue-600 hover:text-blue-800 text-sm"
                      title="Xem chi tiết công việc"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
                <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Xác nhận phản ánh
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Vui lòng nhập hạn xử lý cho phản ánh này.
              </p>
              <div className="mb-4">
                <input
                  type="datetime-local"
                  value={deadline}
                  onChange={(e) => setDeadline(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min={new Date().toISOString().slice(0, 16)}
                />
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowConfirmModal(false);
                    setDeadline('');
                  }}
                  className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                >
                  Hủy
                </button>
                <button
                  onClick={confirmFeedback}
                  disabled={confirming || !deadline}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {confirming ? 'Đang xử lý...' : 'Xác nhận'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Completion Confirmation Modal */}
      {showCompletionConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Xác nhận hoàn thành
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Bạn có chắc chắn muốn hoàn thành phản ánh này? Hành động này không thể hoàn tác.
              </p>
              <div className="text-lg font-semibold text-blue-600 mb-4">
                {completionCountdown} giây
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowCompletionConfirm(false)}
                  className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                >
                  Hủy
                </button>
                <button
                  onClick={confirmCompleteFeedback}
                  disabled={completing || completionCountdown > 0}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {completing ? 'Đang xử lý...' : 'Xác nhận'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Image Modal */}
      {imageModalOpen && selectedImage && (
        <ImageModal
          isOpen={imageModalOpen}
          onClose={closeImageModal}
          fileName={selectedImage}
          fileUrl={selectedImage}
        />
      )}

      {/* Sub-task Creation Modal */}
      {showSubTaskModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4">Tạo công việc con</h3>
            <form onSubmit={handleSubTaskSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Tiêu đề</label>
                <input
                  type="text"
                  value={subTaskTitle}
                  onChange={e => setSubTaskTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Mô tả</label>
                <textarea
                  value={subTaskDescription}
                  onChange={e => setSubTaskDescription(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Người thực hiện</label>
                <select
                  value={subTaskAssignee}
                  onChange={e => setSubTaskAssignee(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Chọn người thực hiện</option>
                  {staffList.map(staff => (
                    <option key={staff.id} value={staff.id}>{staff.name} ({staff.email})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Hạn hoàn thành</label>
                <input
                  type="datetime-local"
                  value={subTaskDueDate}
                  onChange={e => setSubTaskDueDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                  min={new Date().toISOString().slice(0, 16)}
                />
              </div>
              <div className="flex space-x-3 mt-4">
                <button
                  type="button"
                  onClick={() => setShowSubTaskModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                  disabled={subTaskLoading}
                >
                  Hủy
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={subTaskLoading}
                >
                  {subTaskLoading ? 'Đang tạo...' : 'Tạo công việc'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedbackDetails; 